import { Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { UtilService } from '../../../../services/util.service';
import { ChartComponent, PieChartModel } from '@ngx-seatback/charts';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../../../services/user.service';
import { TagService } from '../../../../services/tag.service';
import { Pivot } from '../../../../other/pivot/PivotConfig';
import { ProductDTO } from 'src/app/services/api/api';
import { InfoCardComponent } from '../../../../components/info-card/info-card.component';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';
import { DateTransformationService } from '../../../../services/transformation-helper/date-transformation.service';

@Component({
  selector: 'sb-comp-share-of-voice-overview',
  templateUrl: './share-of-voice-overview.component.html',
  styleUrls: ['./share-of-voice-overview.component.scss'],
  standalone: false,
})
export class ShareOfVoiceOverviewComponent {
  readonly stringTransformation = inject(StringTransformationService);
  readonly dateTransformation = inject(DateTransformationService);
  _reportingObj: any[];
  _additionalReportingObj: any[];
  onlyMediumElement: any;
  pieChartData: PieChartModel;

  constructor(
    public readonly util: UtilService,
    private readonly translate: TranslateService,
    public readonly userStatus: UserService,
    public readonly tagService: TagService
  ) {
    this._reportingObj = [];
    this._additionalReportingObj = [];
    this.onlyMediumElement = undefined;
    this.pieChartData = {
      title: 'lcdType',
      height: 300,
      width: 100,
      value: 'value',
      category: 'category',
      fontSize: 23,
      sumLabel: false,
      legend: false,
      data: [],
      radius: 60,
    };
  }

  @ViewChild('infoCard') infoCard: InfoCardComponent;
  @ViewChild('pieChartContainer') pieChartContainer: ChartComponent;

  @Output() selectedElement = new EventEmitter<number>();

  @Input() selectedField: string;
  @Input() loading: boolean;
  _playoutsNumber: boolean;
  @Input() set playoutsNumber(val: boolean) {
    this._playoutsNumber = val;
    this.buildPieChart();
  }

  get playoutsNumber(): boolean {
    return this._playoutsNumber;
  }

  @Input() from;
  @Input() till;

  @Input() set additionalReportingObj(val) {
    this._additionalReportingObj = val;
    if (val) {
      this.additionalReportingObj.sort((recordA, recordB) => {
        if (recordA.report && recordB.report) {
          return recordA.report.aggregated.v0 > recordB.report.aggregated.v0 ? -1 : 1;
        }
        return -1;
      });
    }
    this.buildPieChart();
  }

  get additionalReportingObj() {
    return this._additionalReportingObj;
  }

  @Input() set reportingObj(val) {
    if (val.length > 0) {
      if (val[val.length - 1].element === Pivot.EMPTY_VALUE) {
        this.onlyMediumElement = val[val.length - 1];
        if (val.length > 0) {
          val = val.slice(0, val.length - 1);
        }
      } else {
        this.onlyMediumElement = undefined;
      }
    }
    this._reportingObj = val;
    this.reportingObj.sort((recordA, recordB) => {
      if (recordA.report && recordB.report) {
        return recordA.report.aggregated.v0 > recordB.report.aggregated.v0 ? -1 : 1;
      }
      return -1;
    });
    this.buildPieChart();
  }

  get reportingObj() {
    return this._reportingObj;
  }

  calcPercentageVal(val: number, base: number) {
    if (val && base) {
      return Math.round((val / base) * 100) + '%';
    }
    return 0;
  }

  buildPieChart() {
    this.pieChartData = {
      title: 'lcdType',
      height: 300,
      width: 100,
      value: 'value',
      category: 'category',
      fontSize: 23,
      sumLabel: false,
      legend: false,
      data: [],
      radius: 60,
    };
    if (this._reportingObj && this.reportingObj.length > 0) {
      this.fillPieChartDataWithReportingObj();
    }
    if (this.additionalReportingObj && this.additionalReportingObj.length > 0) {
      this.fillPieChartDataWithAdditionalReportingObj();
    }
    let sum: string;
    const sumOfPieChartValues = (
      this.pieChartData.data as {
        category: string;
        value: number;
        color: string;
      }[]
    ).reduce((sum: number, item) => sum + item.value, 0);
    if (!this.playoutsNumber) {
      sum = this.dateTransformation.parseTimeToDuration(sumOfPieChartValues);
    } else {
      sum = this.stringTransformation.truncateNumber(sumOfPieChartValues);
    }
    this.pieChartData.customLabel = '[#676c7180 font-size: 16px]' + this.translate.instant('lang.reporting.playouts') + '[/]\n' + sum;
    if (this.pieChartContainer) {
      this.pieChartContainer.rerenderChart(this.pieChartData);
    }
  }

  fillPieChartDataWithReportingObj() {
    if (this._reportingObj && this.reportingObj.length > 0) {
      for (const ele of this._reportingObj) {
        const tempObj = {
          category: ele.element.name,
          value: null,
          color: '#' + this.util.renderStringColor(ele.element.name),
        };
        if (!this.playoutsNumber) {
          tempObj.value = ele.report.aggregated.v1;
        } else {
          tempObj.value = ele.report.aggregated.v0;
        }
        this.pieChartData.data.push(tempObj);
      }
    }
  }

  fillPieChartDataWithAdditionalReportingObj() {
    if (this.additionalReportingObj && this.additionalReportingObj.length > 0) {
      for (const ele of this.additionalReportingObj) {
        const tempObj = {
          category: ele.element.name,
          value: null,
          color: '#efefef',
        };
        if (!this.playoutsNumber) {
          tempObj.value = ele.report.aggregated.v1;
        } else {
          tempObj.value = ele.report.aggregated.v0;
        }
        this.pieChartData.data.push(tempObj);
      }
    }
  }

  selectElement($event) {
    this.loading = true;
    setTimeout(() => {
      this.selectedElement.emit($event);
    }, 100);
  }

  getOnlyMotiveElementText(listElement: boolean): string {
    if (!listElement) {
      return this.translate.instant('lang.globals.elementsSplittedIn');
    }

    const fieldTranslations: Record<string, string> = {
      mediumGroup: 'lang.globals.mediumGroup',
      medium: 'lang.globals.medias',
      product: 'lang.globals.product',
      productCategory: 'lang.globals.productCategory',
    };

    const translationKey = fieldTranslations[this.selectedField] ?? '';

    return this.translate.instant('lang.globals.elementsWithout') + this.translate.instant(translationKey);
  }

  renderProductCode(product: ProductDTO): string {
    if (product.identifier) {
      return product.identifier;
    }
    if (product.gtin) {
      return product.gtin.toString();
    }
    if (product.plu) {
      return product.plu.toString();
    }
    if (product.asin) {
      return product.asin.toString();
    }
    return '';
  }

  setHoverStatus(reportingObjOrigin: number, index: number, status: boolean) {
    switch (reportingObjOrigin) {
      case 0: // origin reportingObject
        this.infoCard.setHoverStatus(index, status, false);
        this.pieChartContainer.setHoverStatus(index, status);
        break;
      case 1: // origin onlyMediumObject
        for (let i = 0; i <= this.additionalReportingObj.length - 1; i++) {
          this.pieChartContainer.setHoverStatus(this.reportingObj.length + i, status);
        }
        break;
      case 2: // origin additionalReportingObj
        this.infoCard.setHoverStatus(index + this.reportingObj.length, status, true);
        this.pieChartContainer.setHoverStatus(index + this.reportingObj.length, status);
        break;
      case -1:
      default:
        break;
    }
  }

  getReportingObjForInfoCard(): any[] {
    const res = this._reportingObj.slice();
    for (const ele of this._additionalReportingObj) {
      res.push(ele);
    }
    return res;
  }
}
